import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, error } = useAuthStore();
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch {
      // The auth store retains and displays the actual server error.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #050505 0%, #0a0a0a 30%, #111111 60%, #0a0808 100%)' }}>

      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gold radial glow */}
        <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(212,168,83,0.08) 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(212,168,83,0.05) 0%, transparent 70%)' }} />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'linear-gradient(rgba(212,168,83,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212,168,83,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
      </div>

      <div className="w-full max-w-[420px] relative z-10">
        {/* Logo & Brand */}
        <div className="text-center mb-8">

          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: '#f5f5f5' }}>
            Rupyo <span style={{ color: '#d4a853' }}>Pay</span>
          </h1>
          <p className="text-sm" style={{ color: '#666' }}>Admin Control Panel</p>
        </div>

        {/* Login Card */}
        <div className="rounded-2xl p-6 sm:p-8 relative overflow-hidden"
          style={{ background: 'linear-gradient(145deg, #141414 0%, #1a1a1a 100%)', border: '1px solid #2a2a2a', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>

          {/* Card top accent line */}
          <div className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: 'linear-gradient(90deg, transparent, #d4a853, transparent)' }} />

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#808080' }}>
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: '#555' }} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 outline-none"
                  style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', color: '#e5e5e5' }}
                  placeholder="admin@rupyopay.com"
                  required
                  disabled={isSubmitting}
                  onFocus={(e) => { e.target.style.borderColor = '#d4a853'; e.target.style.boxShadow = '0 0 0 3px rgba(212,168,83,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#2a2a2a'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#808080' }}>
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: '#555' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-11 pr-12 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 outline-none"
                  style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', color: '#e5e5e5' }}
                  placeholder="••••••••"
                  required
                  disabled={isSubmitting}
                  onFocus={(e) => { e.target.style.borderColor = '#d4a853'; e.target.style.boxShadow = '0 0 0 3px rgba(212,168,83,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#2a2a2a'; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 transform -translate-y-1/2 p-1 rounded-lg transition-colors"
                  style={{ color: '#555' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#d4a853')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#555')}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            {error && (
              <div role="alert" className="rounded-lg border border-red-400/25 bg-red-500/10 px-3 py-2.5 text-sm text-red-200">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 mt-2 disabled:opacity-50"
              style={{
                background: isSubmitting ? '#333' : 'linear-gradient(135deg, #c49a3a 0%, #d4a853 50%, #e8c36e 100%)',
                color: '#0a0a0a',
                boxShadow: isSubmitting ? 'none' : '0 4px 20px rgba(212,168,83,0.3)',
              }}
              onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.boxShadow = '0 6px 28px rgba(212,168,83,0.45)'; }}
              onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.boxShadow = '0 4px 20px rgba(212,168,83,0.3)'; }}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs" style={{ color: '#444' }}>
            Secure admin access • <span style={{ color: '#d4a853' }}>Rupyo Pay</span> Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
